import React, { useState, useRef, useEffect, createContext } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom'; // Import useLocation
import { Alert, Spinner } from 'flowbite-react';

import {
    HiArrowSmRight,
    HiMenuAlt2,
    HiHome,
    HiOutlineUsers,
    HiUserCircle,
    HiOfficeBuilding,
    HiOutlineMail,
    HiOutlineGlobeAlt,
    HiOutlineSpeakerphone ,
    HiOutlineUserGroup,
    HiOutlineDocumentText,
    HiOutlineSparkles 
} from 'react-icons/hi';

import CompanyLogo from '../assets/img/logo.png';

import { logout } from '../services/authService';

import SidebarItem from './components/SidebarItem';


const AdminLayout = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const profileDropdownRef = useRef(null);
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null); // Define currentUserId state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const location = useLocation(); // Get current location

    // Initialize currentUserId when the component mounts
    useEffect(() => {
        document.title = "Chat with CPA";
        const userString = localStorage.getItem('user');
        if (userString) {
            try {
                const user = JSON.parse(userString);
                console.log("AdminLayout: Logged-in user:", user);
                setCurrentUserId(user.id);
            } catch (err) {
                console.error("AdminLayout: Error parsing user data from localStorage:", err);
            }
        }
    }, []);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const toggleProfileDropdown = () => {
        setIsProfileDropdownOpen(!isProfileDropdownOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
                setIsProfileDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [profileDropdownRef]);

    const handleLogout = async () => {
        await logout(navigate, setLoading, setError, setIsProfileDropdownOpen);
    };

    return (
            <div className="flex min-h-screen admin-header-gradient shadow-md ">
                <aside
                    className={`fixed top-0 left-0 w-64 h-full z-30 flex flex-col
                    transform transition-transform duration-300 ease-in-out border border-gray-200 border-r
                    ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
                >
                    <div className="p-4 border-b border-gray-100 border-r h-16">
                        <Link to="/dashboard" className="flex items-center space-x-3 rtl:space-x-reverse">
                            <img
                                src={CompanyLogo}
                                className="h-8"
                                alt="CPA Admin Panel"
                            />
                            <span className="self-center text-xl font-semibold whitespace-nowrap ">
                                CPA Admin
                            </span>
                        </Link>
                    </div>

                    <div className="flex-1 overflow-y-auto py-4 px-3">
                        <ul className="space-y-2 ">
                            <SidebarItem
                                url="/dashboard"
                                value="Dashboard"
                                IconComponent={HiHome}
                            />

                            <SidebarItem
                                url="/dashboard/activity-log"
                                value="Activity Logs"
                                IconComponent={HiOutlineMail}
                            />

                            <SidebarItem
                                url="/dashboard/customer-list"
                                value="Customers"
                                IconComponent={HiOutlineUsers}
                            />
                            <SidebarItem
                                url="/dashboard/customer-email"
                                value="Emails"
                                IconComponent={HiOutlineMail}
                            />
                            <SidebarItem
                                url="/dashboard/chat"
                                value="Messages"
                                IconComponent={HiOutlineMail}
                            />


                             <SidebarItem
                                url="/dashboard/manage-website"
                                value="Manage Website"
                                IconComponent={HiOutlineGlobeAlt}
                            />

                             <SidebarItem
                                url="/dashboard/marketing"
                                value="Marketing"
                                IconComponent={HiOutlineSpeakerphone}
                            />

                             <SidebarItem
                                url="/dashboard/customer-finder"
                                value="Customer Finder"
                                IconComponent={HiOutlineUserGroup }
                            />

                             <SidebarItem
                                url="/dashboard/document-training"
                                value="Document/Training"
                                IconComponent={HiOutlineDocumentText}
                            />


                            <SidebarItem
                                url="/dashboard/new-feature"
                                value="New Feature"
                                IconComponent={HiOutlineSparkles}
                            />

                            

                         
                        </ul>
                    </div>
                </aside>

                <div
                    className={`flex-1 flex flex-col
                        transition-all duration-300 ease-in-out
                        ${isSidebarOpen ? 'ml-64' : 'ml-0'}`}
                >
                    <header className="sticky top-0 py-4 px-4 md:px-6 z-20 flex justify-between items-center h-16 bg-white border-b border-gray-200">
                        <button
                            onClick={toggleSidebar}
                            className=" hover:bg-sidebar-hover focus:outline-none focus:ring-4 focus:sidebar-hover rounded-lg p-2.5"
                        >
                            <HiMenuAlt2 className="w-6 h-6" aria-label="Toggle sidebar" />
                        </button>

                        <div className="flex items-center space-x-4">
                            <div className="relative" ref={profileDropdownRef}>
                                <button
                                    onClick={toggleProfileDropdown}
                                    type="button"
                                    className="flex items-center hover:bg-sidebar-hover focus:outline-none focus:ring-4 focus:ring-sidebar-hover rounded-full p-1.5"
                                    aria-expanded={isProfileDropdownOpen}
                                >
                                    <HiUserCircle className="w-8 h-8" />
                                </button>

                                {isProfileDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-40">
                                        <Link
                                            to="/dashboard/cpa-profile"
                                            onClick={() => setIsProfileDropdownOpen(false)}
                                            className="flex items-center px-4 py-2 text-sm hover:bg-sidebar-hover "
                                        >
                                            <HiOfficeBuilding className="w-5 h-5 mr-2" /> Company Profile
                                        </Link>
                                        <div className="border-t border-gray-100 my-1"></div>
                                        <button
                                            onClick={handleLogout}
                                            disabled={loading}
                                            className="flex items-center w-full text-left px-4 py-2 text-sm hover:bg-sidebar-hover"
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner aria-label="Spinner button example" size="sm" className="mr-2" />
                                                    <span>Logging out...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <HiArrowSmRight className="w-5 h-5 mr-2 transform rotate-180" /> Logout
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </header>

                    {error && (
                        <div className="px-4 pt-4 md:px-6 md:pt-6 lg:px-8 lg:pt-8">
                            <Alert color="failure" onDismiss={() => setError(null)}>
                                <span className="font-medium">Logout Error!</span> {error}
                            </Alert>
                        </div>
                    )}

                    <main className="flex-1 ">
                        <div className="bg-white min-h-[calc(100vh-128px)]">
                            {/* Pass shouldRefreshMessages and a way to reset it to Outlet children */}
                            <Outlet  />
                        </div>
                    </main>
                </div>

                {isSidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black opacity-50 z-20 md:hidden"
                        onClick={toggleSidebar}
                    ></div>
                )}
            </div>
    );
};

export default AdminLayout;